import type { CfoFundingLine, CfoFundingReceipt } from "@/api/types";

/** Why a file was refused at upload (backend CSV parser codes). */
export const CSV_ERRORS: Record<string, string> = {
  AMBIGUOUS_DELIMITER_OR_MISSING_HEADERS:
    "This is not a Crunchbase Funding Rounds export: the required columns are missing.",
  DUPLICATE_HEADER: "A column appears twice in the file.",
  EMPTY_FILE: "The file is empty.",
  FILE_TOO_LARGE: "The file is larger than 5 MB.",
  ROW_LIMIT: "The file has more than 1,000 rows. Split it into smaller exports.",
  COLUMN_LIMIT: "The file has too many columns.",
  CELL_LIMIT: "A cell of the file is too long.",
  DIAGNOSTIC_LIMIT: "The file has too many invalid values to be read.",
  INVALID_CSV_STRUCTURE: "The file is not a valid CSV.",
  INVALID_ROW_WIDTH: "Some rows do not have the same number of columns as the header.",
  INVALID_UTF8: "The file is not encoded in UTF-8.",
  NORMALIZED_RECEIPT_LIMIT: "The file is too large to be previewed.",
  CFO_CSV_RAW_UPLOAD_REQUIRED: "The file must be sent as a CSV.",
};

/** How each company of the file matches the CRM (preview identity). */
export type Match = "new" | "known" | "check";
export const MATCH: Record<Match, { label: string; className: string }> = {
  new: { label: "New company", className: "bg-indigo-50 text-indigo-700 ring-indigo-200" },
  known: { label: "Known company", className: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  check: { label: "To check", className: "bg-amber-50 text-amber-800 ring-amber-200" },
};

export function matchOf(line: CfoFundingLine): Match {
  const reason = String(line.result.reason ?? "");
  if (reason === "NEW") return "new";
  if (["ESTABLISHED", "DOMAIN_MATCH", "SOURCE_RECORDED"].includes(reason)) return "known";
  return "check";
}

/** Why a company group needs a person, before or after it is applied. */
export const REASONS: Record<string, string> = {
  NEW: "New company",
  ESTABLISHED: "Known company",
  DOMAIN_MATCH: "Known company, matched by its website",
  EXISTING_NOT_ADMITTED: "Known company outside the CFO program",
  DOMAIN_CHANGE_REVIEW: "The website differs from the CRM",
  DOMAIN_ALIAS_REVIEW: "The website may be an alias of another company",
  DOMAIN_IDENTITY_CONFLICT: "The website belongs to another company",
  CONTRADICTORY_COMPANY_ROWS: "The rows of this company disagree on its website",
  FINANCIAL_CORRECTION_REVIEW: "The amounts differ from the recorded round",
  EXPLICIT_CLEAR_REQUIRED: "The file empties a recorded value",
  REFERENCE_ROUND_AMBIGUOUS: "Several rounds could be the reference round",
  COMPANY_CHANGED_SINCE_PREVIEW: "The company changed since the preview",
  ROUND_VERSION_CONFLICT: "The round changed since the preview",
  SQL_GROUP_ROLLED_BACK: "A technical failure stopped this company",
  WON_CLIENT_INCONSISTENT: "The company is already a won client",
  CANCELLED_BEFORE_GROUP_COMMIT: "Cancelled before being recorded",
  SOURCE_DATA_REVIEW: "Some values of the file are invalid",
  SOURCE_RECORDED: "Recorded",
  CONFIRMED: "Waiting",
};

export function reasonLabel(code: unknown): string {
  const value = String(code ?? "");
  return REASONS[value] ?? value.replace(/_/g, " ").toLowerCase();
}

/** Row-level problems the parser noted (backend diagnostic codes). */
export function diagnosticLabel(diagnostic: Record<string, string>): string {
  const field = diagnostic.field ? ` (${diagnostic.field})` : "";
  switch (diagnostic.code) {
    case "REQUIRED_VALUE_MISSING":
      return `Missing value${field}`;
    case "INVALID_CRUNCHBASE_REFERENCE":
      return `Invalid Crunchbase link${field}`;
    case "INVALID_WEBSITE":
      return "Invalid website";
    case "INVALID_MONEY":
      return `Invalid amount${field}`;
    case "INVALID_CURRENCY":
      return `Invalid currency${field}`;
    case "INVALID_COUNT":
      return `Invalid number${field}`;
    case "FUTURE_ANNOUNCED_DATE":
      return "Announced in the future";
    case "INVALID_DATE":
      return "Invalid date";
    case "UNKNOWN_FUNDING_STAGE":
      return "Unknown funding stage";
    case "UNKNOWN_FUNDING_TYPE":
      return "Unknown round type";
    case "FIELD_TOO_LONG":
      return `Value too long${field}`;
    case "NAME_TOO_LONG":
      return "Company name too long";
    default:
      return `${(diagnostic.code ?? "Issue").replace(/_/g, " ").toLowerCase()}${field}`;
  }
}

/** Compact USD: "$2.5M". */
export function usd(value: string | undefined | null): string | null {
  if (!value) return null;
  const amount = Number(String(value).replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(amount) || amount <= 0) return null;
  for (const [size, suffix] of [
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "K"],
  ] as const) {
    if (amount >= size) {
      return `$${(amount / size).toFixed(1).replace(/\.0$/, "")}${suffix}`;
    }
  }
  return `$${amount.toFixed(0)}`;
}

export function websiteOf(cells: Record<string, string>): string | null {
  const raw = cells["Organization Website"];
  if (!raw) return null;
  return raw.replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/$/, "");
}

/** Import progress from the receipt: company groups settled out of all. */
export function progressOf(receipt: CfoFundingReceipt) {
  const total = receipt.counters.companies ?? 0;
  const applied = receipt.counters.groups_applied ?? 0;
  const review = receipt.counters.groups_review ?? 0;
  const rejected = receipt.counters.groups_rejected ?? 0;
  const done = Math.min(total, applied + review + rejected);
  return { total, done, applied, review, rejected, pct: total ? done / total : 0 };
}

/** The import has settled when no group is left to record. */
export function isSettled(receipt: CfoFundingReceipt): boolean {
  if (receipt.state === "PREVIEW") return false;
  if (receipt.state === "CANCELLED") return true;
  return receipt.state !== "CONFIRMED" && progressOf(receipt).done >= progressOf(receipt).total;
}

/** The local re-evaluation that follows the recording of the rounds. */
export const EVALUATION: Record<string, { label: string; tone: "busy" | "done" | "wait" | "fail" }> = {
  NOT_SCHEDULED: { label: "Starts once the rounds are recorded", tone: "wait" },
  PENDING_LOCAL_WORK: { label: "Re-evaluating the companies…", tone: "busy" },
  // The cohort is re-ranked, then every company is re-scored; on a project without
  // the service mandate that waits for someone allowed to run the engine.
  COHORT_QUEUED: {
    label: "Cohort re-ranking queued: companies are re-scored when it runs",
    tone: "wait",
  },
  COMPLETED: { label: "Companies re-evaluated", tone: "done" },
  UNCHANGED_NO_RECALCULATION: { label: "Nothing changed, no re-evaluation needed", tone: "done" },
  POLICY_OR_SERVICE_MANDATE_REQUIRED: {
    label: "Waiting: automatic re-evaluation needs the service mandate",
    tone: "wait",
  },
  FAILED: { label: "Re-evaluation failed for some companies", tone: "fail" },
};

export const RECEIPT_STATES: Record<string, { label: string; className: string }> = {
  PREVIEW: { label: "Preview", className: "bg-slate-100 text-slate-600" },
  CONFIRMED: { label: "Importing", className: "bg-indigo-50 text-indigo-700" },
  APPLIED: { label: "Imported", className: "bg-emerald-50 text-emerald-700" },
  PARTIAL: { label: "Partly imported", className: "bg-amber-50 text-amber-800" },
  REVIEW: { label: "To review", className: "bg-amber-50 text-amber-800" },
  CANCELLED: { label: "Cancelled", className: "bg-slate-100 text-slate-500" },
};

export function fileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}
